import React, { useState, useMemo, useEffect } from 'react';
import {
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Box,
  Chip,
  Typography,
  CircularProgress,
  Tooltip,
  ListSubheader
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import {
  MachineQuadrant, alternativeOptions, alternativeGroupLabel, AlternativeOptionRow, useReplacementAlternatives,
} from '../shared/ReplacementAlternatives';

// Shared typography styles to match table
const sharedFontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const sharedLetterSpacing = '-0.01em';

const CandidateDropdown = ({
  experimentId,
  experimentType,
  selectedCandidateId,
  candidateProductIds = [],
  candidateNames = [],
  candidateDiscounts = [],
  discount,
  markup = null, // Sólo `alza_precio_sugerido`: % de subida sobre `originalPrice`
  targetPrice = null, // Sólo `alza_precio_sugerido`: precio objetivo absoluto del modelo (p1)
  onDraftChange,
  clientId,
  originalPrice = null, // For discount experiments - shows price calculation
  isFallbackPrice = false, // True if price is from another store (not this specific store)
}) => {
  const { t } = useTranslation();
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newProductValue, setNewProductValue] = useState(null);

  // Product fetching state
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsFetched, setProductsFetched] = useState(false);

  const isDiscountExperiment = experimentType === 'promo_precio_unitario';
  const isReplacementExperiment = experimentType === 'reemplazo_producto' || experimentType === 'ampliar_carril';
  // Subida de precio: ni candidatos ni % editable. Sin esta rama ninguna de las dos de arriba es
  // cierta, `options` queda vacío y la fila caía en la plantilla de reemplazo, enseñando el id
  // crudo del producto (p. ej. "1436343") en el desplegable.
  const isPriceIncreaseExperiment = experimentType === 'alza_precio_sugerido';

  // Local state to track the displayed selection (allows immediate visual feedback)
  const [localSelection, setLocalSelection] = useState(
    isDiscountExperiment ? discount : selectedCandidateId
  );
  // Track the name of a newly added product (for display purposes)
  const [newProductName, setNewProductName] = useState(null);
  // Local state for editable discount input
  const [localDiscount, setLocalDiscount] = useState(discount || '');

  // Reemplazo: alternativas por margen / por volumen y cuadrante de la máquina, pedidas la primera
  // vez que se abre el desplegable (no una petición por fila al montar). Las filas de alerta
  // (`alert-<id>`) aún no son un experimento y se quedan como estaban.
  const [opened, setOpened] = useState(false);
  const replacementAlternatives = useReplacementAlternatives(
    clientId,
    experimentId,
    opened && experimentType === 'reemplazo_producto' && /^\d+$/.test(String(experimentId))
  );
  const alternatives = useMemo(() => alternativeOptions(replacementAlternatives || {}), [replacementAlternatives]);

  // Fetch all products when entering "add new" mode
  useEffect(() => {
    if (isAddingNew && clientId && !productsFetched) {
      const fetchProducts = async () => {
        setProductsLoading(true);
        try {
          const token = localStorage.getItem('access_token');
          const response = await axios.get(
            `${API_BASE_URL}/all-products?clientId=${clientId}`,
            { headers: createAuthHeader(token) }
          );
          const products = response.data.products || [];
          // Map to format expected by Autocomplete
          setAllProducts(products.map(p => ({
            id: p.id,
            label: p.name,
            name: p.name
          })));
          setProductsFetched(true);
        } catch (error) {
          console.error('[CandidateDropdown] Error fetching products:', error);
        } finally {
          setProductsLoading(false);
        }
      };
      fetchProducts();
    }
  }, [isAddingNew, clientId, productsFetched]);

  // Prepare options based on experiment type
  const options = useMemo(() => {
    if (isDiscountExperiment) {
      // For discount experiments, show discount percentages
      return candidateDiscounts.map((disc, index) => ({
        id: `discount_${disc}`,
        label: `${disc}%`,
        value: disc,
      }));
    } else if (isReplacementExperiment) {
      // For replacement experiments, show product names
      return candidateProductIds.map((id, index) => ({
        id,
        label: candidateNames[index] || `Product ${id}`,
        value: id,
      }));
    }
    return [];
  }, [
    isDiscountExperiment,
    isReplacementExperiment,
    candidateDiscounts,
    candidateProductIds,
    candidateNames,
  ]);

  const handleSelectChange = (event) => {
    const value = event.target.value;

    if (value === 'add_new') {
      setIsAddingNew(true);
      return;
    }

    // Update local state immediately for visual feedback
    setLocalSelection(value);

    // Draft change instead of immediate update
    if (onDraftChange) {
      if (isDiscountExperiment) {
        onDraftChange(experimentId, { discount: value });
      } else {
        onDraftChange(experimentId, { candidateProductId: value });
      }
    }
  };

  const handleAutocompleteChange = (event, newValue) => {
    if (newValue && newValue.id) {
      // Update local state immediately for visual feedback
      setLocalSelection(newValue.id);
      // Store the product name for display
      setNewProductName(newValue.label || newValue.name);

      // Draft change with new product
      if (onDraftChange) {
        onDraftChange(experimentId, {
          candidateProductId: newValue.id,
          candidateProductName: newValue.label || newValue.name,
          addToCandidates: true
        });
      }
      setIsAddingNew(false);
      setNewProductValue(null);
    }
  };

  const handleCancelAddNew = () => {
    setIsAddingNew(false);
    setNewProductValue(null);
  };

  // Handle editable discount input change
  const handleDiscountChange = (event) => {
    const value = event.target.value;
    // Allow empty or numeric values (including decimals)
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setLocalDiscount(value);
    }
  };

  // Handle discount blur - save the draft
  const handleDiscountBlur = () => {
    const numericValue = parseFloat(localDiscount);
    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
      if (onDraftChange) {
        onDraftChange(experimentId, { discount: numericValue });
      }
    }
  };

  // Handle Enter key to save discount
  const handleDiscountKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleDiscountBlur();
      event.target.blur();
    }
  };

  // Shared text styles
  const textStyles = {
    fontSize: '12px',
    fontFamily: sharedFontFamily,
    letterSpacing: sharedLetterSpacing,
  };

  // Subida de precio: par de precios de sólo lectura (€0.50 → €0.55  +11%), igual que hace
  // `PriceSection` en las tarjetas desde la Fase 2. No hay desplegable porque no hay nada que
  // elegir: el producto no cambia y el markup lo fija el modelo de elasticidad.
  if (isPriceIncreaseExperiment) {
    const pct = Number(markup);
    const hasPct = Number.isFinite(pct) && pct > 0;
    const base = Number(originalPrice);
    const hasBase = Number.isFinite(base) && base > 0;
    // El p1 del modelo manda; rederivar base × markup (con el % redondeado a entero)
    // puede desviarse un céntimo del precio que dice el titular y el diálogo de aceptar.
    const modelTarget = Number(targetPrice);
    const target = Number.isFinite(modelTarget) && modelTarget > 0
      ? Math.round(modelTarget * 100) / 100
      : (hasPct && hasBase ? Math.round(base * (1 + pct / 100) * 100) / 100 : null);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ...textStyles }}>
        {hasBase && (
          <span style={{ color: '#787774', textDecoration: 'line-through' }}>
            €{base.toFixed(2)}
          </span>
        )}
        {target !== null && (
          <>
            <span style={{ color: '#9CA3AF' }}>→</span>
            <span style={{ color: '#2e7d32', fontWeight: 700 }}>€{target.toFixed(2)}</span>
          </>
        )}
        {hasPct && (
          <Chip
            label={`+${pct}%`}
            size="small"
            sx={{
              height: 18,
              backgroundColor: '#EDF7ED',
              color: '#2e7d32',
              fontWeight: 700,
              fontSize: '11px',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        {!hasPct && !hasBase && (
          <span style={{ color: '#787774', fontStyle: 'italic' }}>
            {t('priceIncrease', { defaultValue: 'Alza de precio' })}
          </span>
        )}
      </Box>
    );
  }

  // If in "add new product" mode, show autocomplete
  if (isAddingNew && isReplacementExperiment) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 260 }}>
        <Autocomplete
          size="small"
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              ...textStyles,
            }
          }}
          value={newProductValue}
          onChange={handleAutocompleteChange}
          options={allProducts}
          loading={productsLoading}
          getOptionLabel={(option) => option.label || option.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('searchProduct', 'Search product...')}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiInputBase-input': textStyles
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {productsLoading ? <CircularProgress color="inherit" size={14} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={productsLoading ? t('loading', 'Loading...') : t('noProductsFound', 'No products found')}
          renderOption={(props, option) => (
            <li {...props} key={option.id} style={{ ...textStyles, padding: '4px 8px' }}>
              {option.label || option.name}
            </li>
          )}
        />
        <Typography
          component="span"
          sx={{
            cursor: 'pointer',
            color: '#787774',
            ...textStyles,
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={handleCancelAddNew}
        >
          {t('cancel', 'Cancel')}
        </Typography>
      </Box>
    );
  }

  // For discount experiments, show editable text field instead of dropdown
  if (isDiscountExperiment) {
    // Calculate the new price based on current discount
    const currentDiscountValue = parseFloat(localDiscount) || 0;
    const newPrice = originalPrice && currentDiscountValue > 0
      ? originalPrice * (1 - currentDiscountValue / 100)
      : originalPrice;
    const hasPrice = originalPrice !== null && originalPrice > 0;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, width: '100%' }}>
        <TextField
          size="small"
          value={localDiscount}
          onChange={handleDiscountChange}
          onBlur={handleDiscountBlur}
          onKeyDown={handleDiscountKeyDown}
          placeholder="0"
          sx={{
            width: 50,
            flexShrink: 0,
            '& .MuiInputBase-input': {
              ...textStyles,
              textAlign: 'center',
              padding: '4px 6px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e0e0e0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#bdbdbd',
            },
          }}
          inputProps={{
            inputMode: 'decimal',
          }}
        />
        <Typography sx={{ ...textStyles, color: '#787774', flexShrink: 0 }}>%</Typography>
        {/* Price calculation display */}
        {hasPrice && (
          <Tooltip
            title={isFallbackPrice ? t('price_from_other_stores', 'Precio de otras máquinas') : ''}
            placement="top"
            arrow
            disableHoverListener={!isFallbackPrice}
          >
            <Typography sx={{
              ...textStyles,
              color: '#9CA3AF',
              fontStyle: 'italic',
              fontSize: '11px',
              marginLeft: '4px',
              whiteSpace: 'nowrap',
              cursor: isFallbackPrice ? 'help' : 'default'
            }}>
              {isFallbackPrice ? '~' : ''}€{originalPrice.toFixed(2)} → €{newPrice.toFixed(2)}
            </Typography>
          </Tooltip>
        )}
      </Box>
    );
  }

  // Normal select dropdown (for replacement experiments)
  return (
    <Select
      size="small"
      value={localSelection || ''}
      onChange={handleSelectChange}
      onOpen={() => setOpened(true)}
      displayEmpty
      sx={{
        width: '100%',
        maxWidth: 260,
        ...textStyles,
        '& .MuiSelect-select': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '4px 8px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#e0e0e0',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#bdbdbd',
        },
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            maxWidth: 260,
          }
        }
      }}
      renderValue={(selected) => {
        if (!selected) {
          return (
            <Typography sx={{ ...textStyles, color: '#787774' }}>
              {t('selectCandidate', 'Select...')}
            </Typography>
          );
        }

        const selectedAlternative = alternatives.find(a => Number(a.productId) === Number(selected));
        const selectedOption = options.find(opt =>
          isDiscountExperiment ? opt.value === selected : opt.id === selected
        ) || (selectedAlternative && { id: selectedAlternative.productId, label: selectedAlternative.productName });

        if (selectedOption) {
          if (isDiscountExperiment) {
            return (
              <Chip
                label={selectedOption.label}
                size="small"
                sx={{
                  height: 20,
                  ...textStyles,
                  '& .MuiChip-label': {
                    padding: '0 6px',
                  }
                }}
                color="primary"
                variant="outlined"
              />
            );
          }
          return (
            <span style={{
              ...textStyles,
              color: '#37352f',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {selectedOption.label}
            </span>
          );
        }

        // If not found in options but we have a newProductName, display it
        if (newProductName && !isDiscountExperiment) {
          return (
            <span style={{
              ...textStyles,
              color: '#37352f',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {newProductName}
            </span>
          );
        }

        return selected;
      }}
    >
      {replacementAlternatives?.quadrant ? (
        <ListSubheader sx={{ lineHeight: '26px' }}>
          <MachineQuadrant quadrant={replacementAlternatives.quadrant} />
        </ListSubheader>
      ) : null}

      <MenuItem value="" disabled sx={textStyles}>
        <Typography sx={{ ...textStyles, color: '#787774' }}>
          {t('selectCandidate', 'Select candidate')}
        </Typography>
      </MenuItem>

      {/* Los candidatos del experimento que no salen en las alternativas siguen arriba, como siempre */}
      {options.filter(option => !alternatives.some(a => Number(a.productId) === Number(option.id))).map((option) => (
        <MenuItem
          key={option.id}
          value={isDiscountExperiment ? option.value : option.id}
          sx={{
            ...textStyles,
            maxWidth: 260,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {isDiscountExperiment ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={option.label}
                size="small"
                sx={{
                  height: 20,
                  ...textStyles,
                  '& .MuiChip-label': {
                    padding: '0 6px',
                  }
                }}
                color="primary"
                variant="outlined"
              />
              <Typography sx={{ ...textStyles, color: '#787774' }}>
                {t('discount', 'Discount')}
              </Typography>
            </Box>
          ) : (
            <span style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}>
              {option.label}
            </span>
          )}
        </MenuItem>
      ))}

      {/* Alternativas del reemplazo por objetivo: Más margen (la primera, la del orquestador) y Más volumen */}
      {['margin', 'volume'].flatMap((group) => {
        const items = alternatives.filter((a) => a.group === group);
        if (items.length === 0) return [];
        return [
          <ListSubheader key={`header-${group}`} sx={{ fontSize: '11px', lineHeight: '28px' }}>
            {alternativeGroupLabel(t, group)}
          </ListSubheader>,
          ...items.map((option) => (
            <MenuItem key={option.key} value={option.productId} sx={{ ...textStyles, maxWidth: 260 }}>
              <AlternativeOptionRow option={option} />
            </MenuItem>
          )),
        ];
      })}

      {/* Un array, no un Fragment: `<Select>` no acepta Fragments como hijos y MUI lo avisaba por
          consola en cada render de detalle de grupo ("The Select component doesn't accept a
          Fragment as a child"). Mismo markup, sin el ruido. */}
      {isReplacementExperiment && [
        <MenuItem key="divider" divider disabled />,
        <MenuItem
          key="add_new"
          value="add_new"
          sx={textStyles}
          onClick={(e) => {
            e.stopPropagation();
            setIsAddingNew(true);
          }}
        >
          <Typography sx={{ ...textStyles, color: '#1976d2' }}>
            + {t('addNewProduct', 'Add new product')}
          </Typography>
        </MenuItem>,
      ]}
    </Select>
  );
};

export default CandidateDropdown;
